import { $aihost, $authHost } from "./";

export const defaultConfig = {
  headers: { "Content-Type": "application/json" },
};

export function CRUD(baseUrl) {
  const API = "/api/v1";

  return {
    search: function (params = {}) {
      return parseResponse(
        $authHost.post(`${API}/${baseUrl}/query`, params, defaultConfig)
      );
    },
    firstOrDefault: async function (params = {}) {
      const response = await this.search(params);
      console.log('response: ' + JSON.stringify(response));
      return response.result.length > 0 ? response.result[0] : null;
    },
    toList: async function (params = {}) {
      const response = await this.search(params);
      return response.result;
    },
    get: function (id) {      
      const uri = baseUrl ? `${API}/${baseUrl}/${id}` : `${API}/${id}`;
      return parseResponse($authHost.get(uri));
    },
    create: function (data) {
      return parseResponse(
         $authHost.post(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    edit: function (data) {
      return parseResponse(
        $authHost.put(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    delete: function (id) {
      return parseResponse(
        $authHost.delete(`${API}/${baseUrl}`, { data: { id } }, defaultConfig)
      );
    },
    workflowRun: function (id, oldStateId, newStateId, comment, directorId) {
      return parseResponse(
        $authHost.post(
          `${API}/${baseUrl}/workflow/run`,
          { id: parseInt(id), oldStateId, newStateId, comment, directorId },
          defaultConfig
        )
      );
    },
    workflowSignRun: function (id, comment) {
      return parseResponse(
        $authHost.post(
          `${API}/${baseUrl}/workflow/sign/run`,
          { id: parseInt(id), comment },
          defaultConfig
        )
      );
    },
    workflowSignRunProtocol: function (request) {
      return parseResponse(
          $authHost.post(
              `${API}/${baseUrl}/workflow/sign/run`,
              request,
              defaultConfig
          )
      );
    },
    post: function (url, params = {}, baseUrl2) {
      const uri = baseUrl ? `${API}/${baseUrl2 || baseUrl}/${url}` : `${API}/${url}`;

      return parseResponse(        
        $authHost.post(uri, params, defaultConfig)
      );
    },
    put: function (url, params = {}, baseUrl2) {
      const uri = baseUrl ? `${API}/${baseUrl2 || baseUrl}/${url}` : `${API}/${url}`;

      return parseResponse(        
        $authHost.put(uri, params, defaultConfig)
      );
    },
  };
}

// Pagination/query adapter for endpoints that use pageNumber/pageSize
// and (sometimes) PascalCase operators (Equals/Asc/etc).
export function CRUDPaged(baseUrl) {
  const API = "/api/v1";

  const toPascalOperator = (op) => {
    if (!op || typeof op !== "string") return op;
    // already looks PascalCase
    if (op[0] === op[0].toUpperCase()) return op;

    const normalized = op.replace(/[\s_-]/g, "").toLowerCase();
    const map = {
      equals: "Equals",
      notequals: "NotEquals",
      like: "Like",
      contains: "Contains",
      startswith: "StartsWith",
      endswith: "EndsWith",
      lessthan: "LessThan",
      lessthanorequal: "LessThanOrEqual",
      greaterthan: "GreaterThan",
      greaterthanorequal: "GreaterThanOrEqual",
      in: "In",
      asc: "Asc",
      desc: "Desc",
    };

    return map[normalized] || (op.charAt(0).toUpperCase() + op.slice(1));
  };

  const normalizePaging = (paging = {}) => {
    // Many legacy callers pass take=null to mean "no limit"
    const legacyTakeIsUnlimited =
      (paging.take === null || paging.take === undefined) &&
      (paging.pageSize === null || paging.pageSize === undefined) &&
      paging.skip !== undefined;

    const pageSize = legacyTakeIsUnlimited ? 10000 : (paging.pageSize ?? paging.take ?? 20);
    const skip = paging.skip ?? 0;
    const pageNumber =
      paging.pageNumber ?? (pageSize ? Math.floor(skip / pageSize) + 1 : 1);

    return {
      pageNumber,
      pageSize,
      returnCount: paging.returnCount ?? true,
    };
  };

  const normalizeFilter = (filter = {}) => {
    if (!filter || typeof filter !== "object") return filter;
    const out = {};
    for (const [key, value] of Object.entries(filter)) {
      if (value == null) continue;
      if (typeof value === "object" && value.operator) {
        out[key] = {
          ...value,
          operator: toPascalOperator(value.operator),
        };
        continue;
      }
      out[key] = value;
    }
    return out;
  };

  const normalizeSort = (sort = {}) => {
    if (!sort || typeof sort !== "object") return sort;
    const keys = Object.keys(sort);
    const out = {};
    keys.forEach((key, idx) => {
      const value = sort[key];
      if (!value) return;
      out[key] = {
        ...value,
        operator: toPascalOperator(value.operator),
        ordinal: value.ordinal ?? idx,
      };
    });
    return out;
  };

  const normalizeQuery = (params = {}) => {
    const { paging, filter, sort, ...rest } = params || {};
    return {
      ...rest,
      paging: normalizePaging(paging ?? { skip: 0, take: null }),
      filter: normalizeFilter(filter),
      sort: normalizeSort(sort),
    };
  };

  const normalizePagedListResponse = (data) => {
    if (!data || typeof data !== "object") return data;
    if (Array.isArray(data.result)) return data;
    if (Array.isArray(data.items)) {
      return {
        ...data,
        result: data.items,
        total:
          data.total ??
          data.totalCount ??
          data.count ??
          (Array.isArray(data.items) ? data.items.length : 0),
      };
    }
    return data;
  };

  return {
    // List endpoints still support /query for advanced filtering.
    search: function (params = {}) {
      const body = normalizeQuery(params);
      return parseResponse(
        $authHost.post(`${API}/${baseUrl}/query`, body, defaultConfig)
      ).then(normalizePagedListResponse);
    },
    // Simple list (pageNumber/pageSize)
    list: function ({
      pageNumber = 1,
      pageSize = 20,
      returnCount = true,
      ...rest
    } = {}) {
      const searchParams = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        returnCount: String(returnCount),
      });
      // pass through optional params (rarely used)
      Object.entries(rest).forEach(([k, v]) => {
        if (v == null) return;
        searchParams.set(k, String(v));
      });
      return parseResponse(
        $authHost.get(`${API}/${baseUrl}?${searchParams.toString()}`)
      ).then(normalizePagedListResponse);
    },
    get: function (id) {
      const uri = baseUrl ? `${API}/${baseUrl}/${id}` : `${API}/${id}`;
      return parseResponse($authHost.get(uri));
    },
    create: function (data) {
      return parseResponse(
        $authHost.post(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    edit: function (data) {
      return parseResponse(
        $authHost.put(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    delete: function (id) {
      return parseResponse(
        $authHost.delete(`${API}/${baseUrl}`, { data: { id } }, defaultConfig)
      );
    },
    post: function (url, params = {}, baseUrl2) {
      const uri = baseUrl
        ? `${API}/${baseUrl2 || baseUrl}/${url}`
        : `${API}/${url}`;

      return parseResponse($authHost.post(uri, params, defaultConfig));
    },
  };
}

export function AiAPI(baseUrl) {
  const API = "/api/v1";

  return {
    search: function (params = {}) {
      return parseResponse(
        $aihost.post(`${API}/${baseUrl}/query`, params, defaultConfig)
      );
    },
    firstOrDefault: async function (params = {}) {
      const response = await this.search(params);
      console.log('response: ' + JSON.stringify(response));
      return response.result.length > 0 ? response.result[0] : null;
    },
    toList: async function (params = {}) {
      const response = await this.search(params);
      return response.result;
    },
    get: function (id) {      
      const uri = baseUrl ? `${API}/${baseUrl}/${id}` : `${API}/${id}`;
      return parseResponse($aihost.get(uri));
    },
    create: function (data) {
      return parseResponse(
        $aihost.post(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    edit: function (data) {
      return parseResponse(
        $aihost.put(`${API}/${baseUrl}`, data, defaultConfig)
      );
    },
    delete: function (id) {
      return parseResponse(
        $aihost.delete(`${API}/${baseUrl}`, { data: { id } }, defaultConfig)
      );
    },
    workflowRun: function (id, oldStateId, newStateId, comment, directorId) {
      return parseResponse(
        $aihost.post(
          `${API}/${baseUrl}/workflow/run`,
          { id: parseInt(id), oldStateId, newStateId, comment, directorId },
          defaultConfig
        )
      );
    },
    workflowSignRun: function (id, comment) {
      return parseResponse(
        $aihost.post(
          `${API}/${baseUrl}/workflow/sign/run`,
          { id: parseInt(id), comment },
          defaultConfig
        )
      );
    },
    workflowSignRunProtocol: function (request) {
      return parseResponse(
        $aihost.post(
              `${API}/${baseUrl}/workflow/sign/run`,
              request,
              defaultConfig
          )
      );
    },
    post: function (url, params = {}, baseUrl2) {
      const uri = baseUrl ? `${API}/${baseUrl2 || baseUrl}/${url}` : `${API}/${url}`;

      return parseResponse(        
        $aihost.post(uri, params, defaultConfig)
      );
    },
  };
}

export function parseResponse(promise) {
  return new Promise((resolve, reject) => {
    promise
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) =>{
        reject(parseError(error))
      } );
  });
}

export function parseResponse2(response) {
  return response.data;
}

function parseError(error) {
  return (
    error.response?.data?.message ||
    (error.response?.data?.errors && error.response?.data?.errors?.length
      ? error.response.data.errors.join(", ")
      : error.message)
  );
}
